import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, MenuItem, TextField, Chip,
  Typography, Box, Alert, Divider,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EditableItemsTable } from '../../components/EditableItemsTable';
import { apiClient } from '../../api/client';
import { boqApi, type Boq, type BoqRevision, type BoqItem, type BoqItemInput } from '../../api/boq.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

type Row = Record<string, unknown>;

export function BoqDetailDialog({ boq, onClose, onChanged }: { boq: Boq | null; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const cost = hasPermission('boq.view_cost');
  const open = boq !== null;

  const [revisions, setRevisions] = useState<BoqRevision[]>([]);
  const [selectedRev, setSelectedRev] = useState<number | null>(null);
  const [revision, setRevision] = useState<(BoqRevision & { items: BoqItem[] }) | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [compareTo, setCompareTo] = useState<number | ''>('');
  const [diff, setDiff] = useState<{ added: BoqItem[]; removed: BoqItem[]; changed: Record<string, unknown>[] } | null>(null);

  const columns = [
    { key: 'description', label: 'Description' },
    { key: 'unit', label: 'Unit', width: 80 },
    { key: 'quantity', label: 'Qty', type: 'number' as const, width: 80 },
    { key: 'clientUnitRate', label: 'Client Rate', type: 'number' as const, width: 110 },
    ...(cost ? [{ key: 'executionUnitRate', label: 'Exec Rate', type: 'number' as const, width: 110 }] : []),
    { key: 'taxPercent', label: 'Tax %', type: 'number' as const, width: 80 },
    { key: 'clientTotalAmount', label: 'Total', readOnly: true, width: 110 },
  ];

  const loadRevision = async (boqId: number, revId: number) => {
    const r = await boqApi.getRevision(boqId, revId);
    setRevision(r);
    setRows((r.items as unknown as Row[]).map((it) => ({ ...it })));
    setSelectedRev(revId);
    setDiff(null);
  };

  useEffect(() => {
    if (!boq) return;
    boqApi.get(boq.id).then(async (full) => {
      const revs = (full as Boq & { revisions: BoqRevision[] }).revisions ?? [];
      setRevisions(revs);
      const current = revs.find((r) => r.isCurrent) ?? revs[0];
      if (current) await loadRevision(boq.id, current.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boq?.id]);

  const refresh = async () => {
    if (boq && selectedRev) await loadRevision(boq.id, selectedRev);
    onChanged();
    qc.invalidateQueries({ queryKey: ['boqs'] });
  };

  const isDraft = revision?.status === 'draft';
  const canEdit = isDraft && hasPermission('boq.update');

  const saveItems = useMutation({
    mutationFn: () => boqApi.setItems(boq!.id, selectedRev!, rows.map((r): BoqItemInput => ({
      description: String(r.description ?? ''), unit: String(r.unit ?? ''),
      quantity: Number(r.quantity ?? 0), clientUnitRate: Number(r.clientUnitRate ?? 0),
      executionUnitRate: Number(r.executionUnitRate ?? 0), taxPercent: Number(r.taxPercent ?? 0),
      remarks: r.remarks ? String(r.remarks) : undefined,
    }))),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const status = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'reject') => boqApi.changeStatus(boq!.id, selectedRev!, action),
    onSuccess: refresh,
    onError: (e) => setError(extractApiError(e)),
  });
  const newRevision = useMutation({
    mutationFn: () => boqApi.createRevision(boq!.id),
    onSuccess: (r) => { onChanged(); boqApi.get(boq!.id).then((full) => setRevisions((full as Boq & { revisions: BoqRevision[] }).revisions ?? [])); loadRevision(boq!.id, r.id); },
    onError: (e) => setError(extractApiError(e)),
  });

  const runCompare = async () => {
    if (!boq || !selectedRev || !compareTo) return;
    setDiff(await boqApi.compare(boq.id, Number(compareTo), selectedRev));
  };

  const importCsv = async () => {
    const text = window.prompt('Paste CSV (headers: CategoryId,SubCategoryId,Description,Unit,Quantity,ClientUnitRate,ExecutionUnitRate,TaxPercent,Remarks)');
    if (!text || !boq || !selectedRev) return;
    try { await boqApi.importCsv(boq.id, selectedRev, text); await refresh(); }
    catch (e) { setError(extractApiError(e)); }
  };

  const exportCsv = async () => {
    if (!boq || !selectedRev) return;
    const res = await apiClient.get(boqApi.exportUrl(boq.id, selectedRev), { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${boq.code}-rev${selectedRev}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!boq) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{boq.code} — {boq.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
          <TextField select size="small" label="Revision" value={selectedRev ?? ''} onChange={(e) => loadRevision(boq.id, Number(e.target.value))} sx={{ minWidth: 220 }}>
            {revisions.map((r) => <MenuItem key={r.id} value={r.id}>Rev {r.revisionNo} · {r.status}{r.isCurrent ? ' · current' : ''}</MenuItem>)}
          </TextField>
          {revision && <Chip size="small" label={revision.status} color={revision.status === 'approved' ? 'success' : revision.status === 'draft' ? 'default' : 'info'} />}
          {hasPermission('boq.update') && <Button size="small" onClick={() => newRevision.mutate()}>New Revision</Button>}
          <Button size="small" onClick={exportCsv}>Export CSV</Button>
          {canEdit && <Button size="small" onClick={importCsv}>Import CSV</Button>}
        </Stack>

        {revision && (
          <Box mb={1}>
            <Typography variant="body2">
              Client value: ₹{revision.totalClientValue?.toLocaleString()} · Client amount: ₹{revision.totalClientAmount?.toLocaleString()}
              {cost && revision.totalProfit !== undefined && ` · Profit: ₹${revision.totalProfit.toLocaleString()} (${revision.marginPct}%)`}
            </Typography>
          </Box>
        )}

        <EditableItemsTable
          columns={columns}
          rows={rows}
          onChange={setRows}
          disabled={!canEdit}
          newRow={() => ({ description: '', unit: '', quantity: 0, clientUnitRate: 0, executionUnitRate: 0, taxPercent: 0, clientTotalAmount: 0 })}
        />

        <Stack direction="row" spacing={1} mt={2}>
          {canEdit && <Button variant="contained" size="small" onClick={() => saveItems.mutate()}>Save Items</Button>}
          {isDraft && hasPermission('boq.update') && <Button size="small" variant="outlined" onClick={() => status.mutate('submit')}>Submit for approval</Button>}
          {revision?.status === 'pending_approval' && hasPermission('boq.approve') && (
            <>
              <Button size="small" color="success" variant="outlined" onClick={() => status.mutate('approve')}>Approve</Button>
              <Button size="small" color="error" variant="outlined" onClick={() => status.mutate('reject')}>Reject</Button>
            </>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2">Compare against</Typography>
          <TextField select size="small" value={compareTo} onChange={(e) => setCompareTo(Number(e.target.value))} sx={{ minWidth: 200 }}>
            {revisions.filter((r) => r.id !== selectedRev).map((r) => <MenuItem key={r.id} value={r.id}>Rev {r.revisionNo}</MenuItem>)}
          </TextField>
          <Button size="small" onClick={runCompare} disabled={!compareTo}>Compare</Button>
        </Stack>
        {diff && (
          <Box mt={1}>
            <Typography variant="body2">Added: {diff.added.length} · Removed: {diff.removed.length} · Changed: {diff.changed.length}</Typography>
            {diff.changed.map((c, i) => <Typography key={i} variant="caption" display="block">{JSON.stringify(c)}</Typography>)}
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
